import { Layout } from 'Layout';
import type { User } from '@repo-level/core-front';
import { AuthContext, CommonContext } from '@repo-level/core-front';
import { ReactComponent as ActiveSessionsIcon } from 'assets/images/profile/activeSessionsIcon.svg';
import { ReactComponent as AddressIcon } from 'assets/images/profile/addressIcon.svg';
import { ReactComponent as BillIcon } from 'assets/images/profile/bill.svg';
import { ReactComponent as CabinetIcon } from 'assets/images/profile/cabinet.svg';
import { ReactComponent as CardIcon } from 'assets/images/profile/card.svg';
import { ReactComponent as ChangePasswordIcon } from 'assets/images/profile/changePasswordIcon.svg';
import { ReactComponent as CoolingOffIcon } from 'assets/images/profile/coolingOffIcon.svg';
import { ReactComponent as CrossIcon } from 'assets/images/profile/crossIcon.svg';
import { ReactComponent as DepositHistoryRowIcon } from 'assets/images/profile/depositHistoryRowIcon.svg';
import { ReactComponent as DepositLimitsIcon } from 'assets/images/profile/depositLimitsIcon.svg';
import { ReactComponent as DocumentIcon } from 'assets/images/profile/document.svg';
import { ReactComponent as DriverLicenceIcon } from 'assets/images/profile/driver-licence.svg';
import { ReactComponent as FaileFileUploadIcon } from 'assets/images/profile/faileFileUploadIcon.svg';
import { ReactComponent as FileUploadIcon } from 'assets/images/profile/fileUploadIcon.svg';
import { ReactComponent as IdentityIcon } from 'assets/images/profile/identityIcon.svg';
import { ReactComponent as ImageIcon } from 'assets/images/profile/imageIcon.svg';
import { ReactComponent as LossLimitsIcon } from 'assets/images/profile/lossLimitsIcon.svg';
import { ReactComponent as PassportIcon } from 'assets/images/profile/passport.svg';
import { ReactComponent as PaymentIcon } from 'assets/images/profile/paymentIcon.svg';
import { ReactComponent as PdfIcon } from 'assets/images/profile/pdfIcon.svg';
import { ReactComponent as SelfExclusionIcon } from 'assets/images/profile/selfExclusionIcon.svg';
import { ReactComponent as TooltipIcon } from 'assets/images/profile/tooltipIcon.svg';
import { ReactComponent as TrashIcon } from 'assets/images/profile/trashIcon.svg';
import { ReactComponent as TwoFactorIcon } from 'assets/images/profile/twoFactorIcon.svg';
import { ReactComponent as VerifyEmailIcon } from 'assets/images/profile/verifyEmailIcon.svg';
import { ReactComponent as VerifyNotificationIcon } from 'assets/images/profile/verifyNotificationIcon.svg';
import { ReactComponent as WagerLimitsIcon } from 'assets/images/profile/wagerLimitsIcon.svg';
import { ReactComponent as WithdrawalHistoryRowIcon } from 'assets/images/profile/withdrawalHistoryRowIcon.svg';
import { Preloader } from 'components/Preloader';
import { AddonsContext } from 'contexts/addons';
import { IntercomContext } from 'contexts/intercom';
import { ModalsContext } from 'contexts/modal';
import { ReferralProgramContext } from 'contexts/referralProgram';
import { TournamentsContext } from 'contexts/tournaments';
import { showTooltip } from 'helpers/tooltip';
import { compose, lazy, mount } from 'navi';
import { Suspense, useContext, useEffect } from 'react';
import { Router, View } from 'react-navi';

export interface ContextRouter {
  isAuthenticated: boolean;
  pendingUser: boolean;
  user: User | undefined;
}

const routes = compose(
  mount({
    '*': lazy(() => import('modules/entrypoint')),
  })
);

const ICONS = {
  notificationIcon: VerifyNotificationIcon,
  identityIcon: IdentityIcon,
  addressIcon: AddressIcon,
  paymentIcon: PaymentIcon,
  fileUploadIcon: FileUploadIcon,
  failedFileUploadIcon: FaileFileUploadIcon,
  crossIcon: CrossIcon,
  verifyEmailIcon: VerifyEmailIcon,
  depositHistoryRowIcon: DepositHistoryRowIcon,
  withdrawalHistoryRowIcon: WithdrawalHistoryRowIcon,
  changePasswordIcon: ChangePasswordIcon,
  twoFactorIcon: TwoFactorIcon,
  activeSessionsIcon: ActiveSessionsIcon,
  depositLimitsIcon: DepositLimitsIcon,
  lossLimitsIcon: LossLimitsIcon,
  coolingOffIcon: CoolingOffIcon,
  wagerLimitsIcon: WagerLimitsIcon,
  selfExclusionIcon: SelfExclusionIcon,
  tooltipIcon: TooltipIcon,
  trashIcon: TrashIcon,
  pdfIcon: PdfIcon,
  imageIcon: ImageIcon,
  billIcon: BillIcon,
  cabinetIcon: CabinetIcon,
  cardIcon: CardIcon,
  documentIcon: DocumentIcon,
  driverLicenceIcon: DriverLicenceIcon,
  passportIcon: PassportIcon,
};

const PROFILE_THEME = {
  white: '#FFFFFF',
  success: '#00B83E',
  successBg: '#00B83E33',
  error: '#F267BA',
  errorBg: '#B81D7A33',
  pending: '#FFCB00',
  pendingBg: '#FFCB0033',
  color1: '#1F202C',
  color2: '#9A9CAC',
  color3: '#FFFFFF',
  color4: '#514DDB',
  color5: '#514DDB33',
  color7: '#323444',
  color9: '#9A9CAC',
  color10: '#00B83E99',
  color11: '#00B83E',
  color12: '#0E111A',
  color20: '#0E111A',
  color22: '#00B83E',
  color23: '#9A9CAC',
  color24: '#00B83E66',
  color25: '#00b83e',
  color27: '#07762f',
  color32: '#514DDB',
  color33: '#FFFFFF',
  color38: '#00B83E',
  color41: '#00B83E',
  color43: '#323444',
  borderRadius1: '12px',
  borderRadius2: '12px',
  ...ICONS,
};

export const Routes = () => {
  const { isAuthenticated, pendingUser, user } = useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);
  const { boot } = useContext(IntercomContext);
  const { gameThemes } = useContext(AddonsContext);
  const { pendingCMSTournaments, cmsTournaments } =
    useContext(TournamentsContext);
  const { project } = useContext(CommonContext);
  const { data: referralProgramData } = useContext(ReferralProgramContext);

  useEffect(() => {
    boot();
  }, [boot]);

  return (
    <Router
      context={{
        isAuthenticated,
        pendingUser,
        user,
        handleModal,
        showTooltip,
        theme: PROFILE_THEME,
        pendingCMSTournaments,
        cmsTournaments,
        gameThemes,
        project,
        referralProgramData,
      }}
      routes={routes}
    >
      <Layout>
        <Suspense fallback={<Preloader />}>
          <View />
        </Suspense>
      </Layout>
    </Router>
  );
};
